import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, switchMap, Observable } from 'rxjs';
import { z } from 'zod';
import { APPCONFIG } from '../config';
import { IdentityTokenService } from '../services/auth/identity-token.service';
import { decodeSnakeCase } from '../utils/operators/decode';
import { Notification, notificationSchema } from '../models/notification';
import { reportAnError } from '../utils/error-handling/error-reporting';
import { retryOnTransientError } from '../utils/operators/retry-on-transient-error';
import { assertSuccess, SimpleActionResponse } from './action-response';

const notificationsResponseSchema = z.object({
  notifications: z.array(z.unknown()),
});

@Injectable({
  providedIn: 'root'
})
export class NotificationHttpService {
  private config = inject(APPCONFIG);
  private http = inject(HttpClient);
  private identityTokenService = inject(IdentityTokenService);

  getNotifications(): Observable<Notification[]> {
    if (!this.config.slsApiUrl) {
      throw new Error('slsApiUrl is not configured');
    }
    return this.identityTokenService.identityToken$.pipe(
      switchMap(token => this.http.get<unknown>(
        `${this.config.slsApiUrl}/notifications`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        { headers: { Authorization: `Bearer ${token}` } }
      ).pipe(
        // Retry transient HTTP failures (network/5xx/timeouts) before parsing, so the bell
        // self-heals after brief outages. Must stay above the schema parsing: a schema failure
        // signals a backend contract change, not a transient issue, and must not be retried.
        retryOnTransientError(),
      )),
      decodeSnakeCase(notificationsResponseSchema),
      map(response => response.notifications.flatMap(entry => {
        const result = notificationSchema.safeParse(entry);
        if (!result.success) {
          reportAnError(result.error);
          return [];
        }
        return [ result.data ];
      })),
    );
  }

  deleteNotification(sk: number): Observable<void> {
    if (!this.config.slsApiUrl) {
      throw new Error('slsApiUrl is not configured');
    }
    return this.identityTokenService.identityToken$.pipe(
      switchMap(token => this.http.delete<SimpleActionResponse>(
        `${this.config.slsApiUrl}/notifications/${sk}`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        { headers: { Authorization: `Bearer ${token}` } }
      )),
      map(assertSuccess),
    );
  }

  deleteAllNotifications(): Observable<void> {
    if (!this.config.slsApiUrl) {
      throw new Error('slsApiUrl is not configured');
    }
    return this.identityTokenService.identityToken$.pipe(
      switchMap(token => this.http.delete<SimpleActionResponse>(
        `${this.config.slsApiUrl}/notifications/all`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        { headers: { Authorization: `Bearer ${token}` } }
      )),
      map(assertSuccess),
    );
  }
}
